import { useState } from "react";
import { fmtMoney, fmtDate, isOverdue } from "@/lib/format";
import { CompanyLogo } from "@/components/common/CompanyLogo";
import { GripVertical } from "lucide-react";

const TYPE_ACCENT = {
  open: "border-t-[hsl(var(--primary))]",
  won: "border-t-[hsl(var(--success))]",
  lost: "border-t-[hsl(var(--danger))]",
};

function Card({ o, onOpen, onDragStart, onDragEnd, dragging }) {
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", o.id); onDragStart(o.id); }}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(o.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(o.id); }}
      className={`group rounded-md border border-border bg-background p-2.5 cursor-grab active:cursor-grabbing hover:border-[hsl(var(--primary)/0.5)] hover:shadow-sm transition-shadow ${dragging ? "opacity-40" : ""}`}
      data-testid={`pipeline-card-${o.id}`}
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="h-3.5 w-3.5 mt-0.5 text-muted-foreground/50 shrink-0 group-hover:text-muted-foreground" />
        <CompanyLogo domain={o.account_domain} website={o.account_website} name={o.account_name} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{o.name}</div>
          <div className="meta truncate">{o.account_name}</div>
          <div className="flex items-center justify-between mt-1.5 gap-2">
            <span className="meta truncate">
              {o.owner_name || <span className="text-[hsl(var(--warning))]">Unassigned</span>}
            </span>
            {o.value_estimate != null && <span className="text-xs font-medium nums shrink-0">{fmtMoney(o.value_estimate, "€")}</span>}
          </div>
          {o.next_action_text ? (
            <div className="meta mt-1 truncate">
              {o.next_action_text}
              {o.next_action_due && <span className={`ml-1 ${isOverdue(o.next_action_due) ? "text-[hsl(var(--warning))]" : ""}`}>· {fmtDate(o.next_action_due)}</span>}
            </div>
          ) : o.on_hold_reason ? (
            <div className="meta mt-1 truncate">On hold: {o.on_hold_reason}</div>
          ) : (
            <div className="mt-1"><span className="chip chip-warning">Next action required</span></div>
          )}
        </div>
      </div>
    </div>
  );
}

export function KanbanBoard({ stages, opps, onOpen, onMove }) {
  const [draggingId, setDraggingId] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const drop = (stageKey) => (e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || draggingId;
    setOverKey(null);
    setDraggingId(null);
    if (id) onMove(id, stageKey);
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-3" data-testid="pipeline-board">
      {stages.map((s) => {
        const items = opps.filter((o) => o.stage === s.key);
        const sum = items.reduce((a, o) => a + (o.value_estimate || 0), 0);
        const isOver = overKey === s.key;
        return (
          <div
            key={s.key}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (overKey !== s.key) setOverKey(s.key); }}
            onDragLeave={(e) => { if (e.currentTarget === e.target) setOverKey(null); }}
            onDrop={drop(s.key)}
            className={`panel border-t-2 ${TYPE_ACCENT[s.type] || TYPE_ACCENT.open} w-[272px] shrink-0 flex flex-col max-h-[calc(100vh-220px)] ${isOver ? "ring-2 ring-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.03)]" : ""}`}
            data-testid={`pipeline-column-${s.key}`}
          >
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-border sticky top-0 bg-card rounded-t-lg">
              <span className="section-title truncate">{s.label}</span>
              <span className="meta nums shrink-0" data-testid={`pipeline-column-count-${s.key}`}>{items.length}{sum > 0 && <span className="ml-1">· {fmtMoney(sum, "€")}</span>}</span>
            </div>
            <div className="p-2 space-y-2 overflow-y-auto flex-1 min-h-[120px]">
              {items.map((o) => (
                <Card key={o.id} o={o} onOpen={onOpen} dragging={draggingId === o.id}
                  onDragStart={setDraggingId} onDragEnd={() => { setDraggingId(null); setOverKey(null); }} />
              ))}
              {items.length === 0 && (
                <div className={`rounded-md border border-dashed border-border py-6 text-center meta ${isOver ? "border-[hsl(var(--primary)/0.5)]" : ""}`}>
                  {isOver ? "Drop here" : "No opportunities"}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
